import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../lib/tenant-context';
import { cn } from '../../lib/utils';
import { ChefHat, Pizza, GlassWater, LayoutDashboard, LogOut, Store, ClipboardList } from 'lucide-react';
const STATIONS = [{
  id: 'waiter',
  name: 'Tomar Orden',
  description: 'POS para meseros y mesas',
  icon: ClipboardList,
  color: 'text-[#F4D35E]',
  bgColor: 'bg-[#F4D35E]/10',
  borderColor: 'border-[#F4D35E]/30',
  hoverBorderColor: 'hover:border-[#F4D35E]/60',
  route: (slug: string) => `/admin/${slug}/waiter`,
  roles: ['owner', 'admin', 'manager', 'caja', 'waiter']
}, {
  id: 'cocina',
  name: 'Cocina',
  description: 'Preparación general de cocina',
  icon: ChefHat,
  color: 'text-amber-500',
  bgColor: 'bg-amber-500/10',
  borderColor: 'border-amber-500/20',
  hoverBorderColor: 'hover:border-amber-500/50',
  route: (slug: string) => `/admin/${slug}/kds?station=cocina`,
  roles: ['owner', 'admin', 'manager', 'caja', 'cocina']
}, {
  id: 'pizzeria',
  name: 'Pizzería',
  description: 'Estación de pizzas',
  icon: Pizza,
  color: 'text-red-500',
  bgColor: 'bg-red-500/10',
  borderColor: 'border-red-500/20',
  hoverBorderColor: 'hover:border-red-500/50',
  route: (slug: string) => `/admin/${slug}/kds?station=pizzeria`,
  roles: ['owner', 'admin', 'manager', 'caja', 'pizzeria']
}, {
  id: 'bebidas',
  name: 'Batidos',
  description: 'Bebidas y batidos',
  icon: GlassWater,
  color: 'text-blue-500',
  bgColor: 'bg-blue-500/10',
  borderColor: 'border-blue-500/20',
  hoverBorderColor: 'hover:border-blue-500/50',
  route: (slug: string) => `/admin/${slug}/kds?station=bebidas`,
  roles: ['owner', 'admin', 'manager', 'caja', 'bebidas']
}, {
  id: 'caja',
  name: 'Caja',
  description: 'Vista completa del restaurante',
  icon: LayoutDashboard,
  color: 'text-emerald-500',
  bgColor: 'bg-emerald-500/10',
  borderColor: 'border-emerald-500/20',
  hoverBorderColor: 'hover:border-emerald-500/50',
  route: (slug: string) => `/admin/${slug}/caja`,
  roles: ['owner', 'admin', 'manager', 'caja']
}];
export function StationSelectPage() {
  const navigate = useNavigate();
  const {
    restaurant,
    role,
    signOut
  } = useTenant();
  const isCapricho = String(restaurant.id) === '2' || restaurant.slug === 'capricho-de-canela';
  // La Cabaña Parrillera (id 24) — only Waiter, Cocina, Batidos y postres, Caja.
  const isCabanaParrillera = String(restaurant.id) === '24' || restaurant.slug === 'la-cabana-parrillera' || restaurant.slug === 'cabana-parrillera';
  // Rancho San Francisco (id 28) — same KDS shape as La Cabaña: no
  // pizzería. Keep the "Bebidas" station name (not renamed).
  const isRanchoSanFrancisco = String(restaurant.id) === '28' || restaurant.slug === 'rancho-san-francisco';
  // NatuCafé Coffee Shop (id 19) — el dueño quiere una experiencia
  // súper simplificada en /operacion: una SOLA tarjeta llamada
  // "Ir a restaurante" que lo lleve directo a /tables. Sin estaciones
  // visibles, sin selección. Resto de restaurantes no se ven afectados.
  const isNatuCafe = String(restaurant.id) === '19' || restaurant.slug === 'natucafe-coffee-shop' || (restaurant.slug?.toLowerCase().includes('natucafe') ?? false);
  // Filter stations based on user role
  let availableStations = STATIONS.filter(station => station.roles.includes(role));
  // NatuCafé — reemplaza por completo la lista de estaciones por una
  // única tarjeta "Ir a restaurante" que apunta a /tables. Se hace
  // ANTES de los overrides de Cabaña / Rancho porque esos no aplican.
  if (isNatuCafe) {
    availableStations = [{
      id: 'natucafe-tables',
      name: 'Ir a restaurante',
      description: 'Ver mesas, tomar órdenes y cobrar',
      icon: Store,
      color: 'text-[#C6A969]',
      bgColor: 'bg-[#C6A969]/10',
      borderColor: 'border-[#C6A969]/30',
      hoverBorderColor: 'hover:border-[#C6A969]/60',
      route: (slug: string) => `/admin/${slug}/tables`,
      roles: ['owner', 'admin', 'manager', 'caja', 'waiter']
    }];
  }
  // Per-restaurant station overrides — surgical, do not affect other restaurants
  if (!isNatuCafe && isCabanaParrillera) {
    availableStations = availableStations.filter(s => s.id !== 'pizzeria').map(s => s.id === 'bebidas' ? {
      ...s,
      name: 'Batidos y postres',
      description: 'Batidos, postres y bebidas'
    } : s);
  } else if (!isNatuCafe && isRanchoSanFrancisco) {
    // Rancho: hide pizzería (the restaurant doesn't serve pizza) but
    // keep every other station as-is.
    availableStations = availableStations.filter(s => s.id !== 'pizzeria');
  }
  // Capricho: skip station select, go straight to Caja
  useEffect(() => {
    if (isCapricho) {
      navigate(`/admin/${restaurant.slug}/caja`, {
        replace: true
      });
      return;
    }
    // Rancho San Francisco: el dueño siempre arranca en la pantalla
    // de Mesas (esa es su "home"). Saltamos el selector de estación.
    // EXCEPCIÓN: los meseros (role='waiter') van directo a "Tomar Orden"
    // (WaiterPOSPage). Su acceso está limitado a crear pedidos y
    // mandarlos a cocina — no tienen permiso para ver el resto del admin.
    if (isRanchoSanFrancisco) {
      const ranchoTarget = role === 'waiter' ? `/admin/${restaurant.slug}/waiter` : `/admin/${restaurant.slug}/tables`;
      navigate(ranchoTarget, {
        replace: true
      });
      return;
    }
    // NatuCafé — el dueño explícitamente quiere VER la tarjeta
    // "Ir a restaurante" en /operacion (no auto-redirigir). Por eso
    // saltamos el auto-redirect cuando hay una sola estación si es
    // NatuCafé. Para todos los demás casos de una sola estación
    // (p. ej. un usuario rol "cocina" que solo ve "Cocina"), seguimos
    // auto-redirigiendo como antes.
    if (availableStations.length === 1 && !isNatuCafe) {
      navigate(availableStations[0].route(restaurant.slug), {
        replace: true
      });
    }
  }, [isCapricho, availableStations.length, navigate, restaurant.slug, isNatuCafe]);
  // If auto-redirecting, show a minimal loading state. NatuCafé no entra
  // por esta rama porque queremos que el dueño vea la tarjeta visible.
  if (isCapricho || availableStations.length === 1 && !isNatuCafe) {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-6031">
        <div className="w-12 h-12 border-4 border-white/10 border-t-white/50 rounded-full animate-spin" data-id="element-6032" />
      </div>;
  }
  return <div className="min-h-screen bg-[#0B0B0B] flex flex-col items-center justify-center p-4 sm:p-6 font-sans" data-id="element-6033">
      <div className="w-full max-w-2xl mx-auto flex flex-col items-center" data-id="element-6034">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-10" data-id="element-6035">
          {restaurant.logo_url ? <img src={restaurant.logo_url} alt={restaurant.name} className="w-20 h-20 rounded-2xl object-cover border border-white/10 shadow-xl mb-4 bg-[#141414]" data-id="element-6036" /> : <div className="w-20 h-20 rounded-2xl bg-[#141414] border border-white/10 shadow-xl flex items-center justify-center mb-4" data-id="element-6037">
              <Store className="w-10 h-10 text-white/50" data-id="element-6038" />
            </div>}

          <div className="flex items-center gap-2 mb-2" data-id="element-6039">
            <h2 className="text-lg font-bold text-white/80" data-id="element-6040">
              {restaurant.name}
            </h2>
            <span className="bg-white/10 text-white/70 px-2 py-0.5 rounded-md text-xs font-bold uppercase tracking-wider" data-id="element-6041">
              {role}
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-2" data-id="element-6042">
            ¿A qué estación querés ingresar?
          </h1>
          <p className="text-base text-white/50" data-id="element-6043">
            Elegí el área de trabajo para continuar
          </p>
        </div>

        {/* Stations Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full mb-12" data-id="element-6044">
          {availableStations.map(station => {
          const Icon = station.icon;
          return <button key={station.id} onClick={() => navigate(station.route(restaurant.slug))} className={cn('flex flex-col items-center text-center p-6 rounded-2xl bg-[#141414] border transition-all duration-200 cursor-pointer active:scale-[0.98]', station.borderColor, station.hoverBorderColor, 'hover:bg-white/5')} data-id="element-6045">
                <div className={cn('w-16 h-16 rounded-full flex items-center justify-center mb-4', station.bgColor, station.color)} data-id="element-6046">
                  <Icon className="w-8 h-8" data-id="element-6047" />
                </div>
                <h3 className="text-xl font-black text-white mb-1" data-id="element-6048">
                  {station.name}
                </h3>
                <p className="text-sm text-white/50" data-id="element-6049">{station.description}</p>
              </button>;
        })}
        </div>

        {/* Footer / Sign Out */}
        <button onClick={signOut} className="flex items-center gap-2 text-sm font-medium text-white/30 hover:text-white/70 transition-colors px-4 py-2 rounded-lg hover:bg-white/5" data-id="element-6050">
          <LogOut className="w-4 h-4" data-id="element-6051" />
          Cerrar sesión
        </button>
      </div>
    </div>;
}